import { useMemo } from 'react';
import { isEmpty, isNil } from 'lodash';
import { useParams } from 'react-router-dom';
import {
  EQUITY_VALUE_CARD_TITLE,
  LIVE_VALUE_CARDS,
  UNREALIZED_FIRM_TOTAL_CARD_TITLE,
} from 'common/constants/valuations';
import { CapIQStatusCard } from 'components/CapIQStatusCard';
import { DEFAULT_CURRENCY, REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { CompanyParams } from 'components/Header/types';
import { ValueCard } from 'components/ValueCard';
import { useHeaderLiveMetrics } from 'context/HeaderLiveMetricsContext';
import { getNumberValue } from 'utilities';
import { ValueCardsProps } from './ValueCards.types';

const ValueCards = (props: ValueCardsProps) => {
  const { company, measurementDate } = props;
  const { tableSlugParam } = useParams<CompanyParams>();

  const shouldRenderValueCards = useMemo(
    () => LIVE_VALUE_CARDS.includes(tableSlugParam!) && !isEmpty(company) && !isEmpty(measurementDate),
    [company, measurementDate, tableSlugParam]
  );

  const { liveEquityValue: currentEquityValue, liveFirmTotal: currentFirmTotal } = useHeaderLiveMetrics();

  const companyCurrency = useMemo(
    () => ({
      code: company?.captable_currency ?? DEFAULT_CURRENCY,
      units: REGULAR_UNIT,
    }),
    [company]
  );
  const isLoadingLiveValues = useMemo(
    () => isNil(currentEquityValue) || isNil(currentFirmTotal),
    [currentEquityValue, currentFirmTotal]
  );

  const equityValue = useMemo(() => getNumberValue(currentEquityValue), [currentEquityValue]);

  const firmTotal = useMemo(() => getNumberValue(currentFirmTotal), [currentFirmTotal]);

  if (!shouldRenderValueCards) {
    return null;
  }

  // Display Header Cards on Companies pages
  return (
    <>
      {/* Cap IQ Health */}
      <CapIQStatusCard />
      {/* Equity Value */}
      <ValueCard
        currency={companyCurrency}
        isLoading={isLoadingLiveValues}
        title={EQUITY_VALUE_CARD_TITLE}
        value={equityValue}
      />
      {/* Firm Total */}
      <ValueCard
        currency={companyCurrency}
        isLoading={isLoadingLiveValues}
        title={UNREALIZED_FIRM_TOTAL_CARD_TITLE}
        value={firmTotal}
      />
    </>
  );
};

export default ValueCards;
